import { useRef } from 'react';
import {
  Avatar,
  Box,
  Button,
  Coin,
  Heading,
  Icon,
  Link,
  List,
  PanelInset,
  PanelSection,
  Paragraph,
  Spinner,
  Text,
} from '@customerio/pluma-components/react';
import plur from 'plur';
import { formatNumber } from '@/utils/number';
import {
  ProfileType,
  type MergedAttributes,
  type MergeSummary,
  type MergeWorkspaceContext,
  type ProfileSource,
} from '../types';
import {
  attributeNamesFromOtherProfile,
  attributesNotPresentInMerge,
  filterAttributes,
  mergeCardAttributes,
  type ProfileAttribute,
} from '../utils/profile-attributes';
import { profileIdentity } from '../utils/profile-identity';
import { ProfileAttributeRow } from './profile-attribute-row';
import * as styles from '../styles.css';

type CustomerProfileCardProps = {
  profileType: ProfileType;
  /** The person shown; for the merged card, the preview's values under the primary's identifiers. */
  profile: ProfileSource;
  context: MergeWorkspaceContext;
  searchTerm: string;
  isLoadingMergedAttributes: boolean;
  mergedAttributes: MergedAttributes;
  /** The preview summary; absent until the preview has loaded. */
  mergeSummary: MergeSummary | undefined;
  /** Clears the selection; the merged card has none. */
  onSelectAnother?: () => void;
};

type ProfileHeaderProps = {
  profile: ProfileSource;
  context: MergeWorkspaceContext;
  onSelectAnother: (() => void) | undefined;
};

function ProfileHeader({ profile, context, onSelectAnother }: ProfileHeaderProps) {
  const email = profile.attributes.email ?? '';
  const { avatarName, heading, contact } = profileIdentity(profile, context);

  return (
    <PanelSection display="flex" alignItems="center">
      <Avatar
        size="xl"
        name={avatarName}
        email={context.withGravatar ? email : undefined}
        withGravatar={context.withGravatar}
        autoColor
        mr="200"
      />
      <Box flex={1} data-test="merge-customer-identity">
        <Heading level="3" className={`${styles.attributeValue} fs-mask`} mb={contact != null ? '050' : undefined}>
          {heading}
        </Heading>
        {contact != null ? (
          <Text color="subtle" className="fs-mask">
            {contact}
          </Text>
        ) : null}
      </Box>
      {onSelectAnother != null ? (
        <Button
          aria-label={`Select another ${context.terms.customersSingular}`}
          variant="tertiary"
          size="sm"
          icon="close"
          isIconOnly
          onClick={onSelectAnother}
        />
      ) : null}
    </PanelSection>
  );
}

type AttributeSummaryProps = {
  profileType: ProfileType;
  attributeCount: number;
  addedCount: number;
  hasSummary: boolean;
  onScrollToAdded: () => void;
};

type SecondarySummaryProps = { addedCount: number; existingCount: number };

function SecondarySummary({ addedCount, existingCount }: SecondarySummaryProps) {
  return (
    <>
      {addedCount > 0 ? (
        <Text display="flex" alignItems="center">
          <Icon name="check-circle" color="success" mr="050" />
          {addedCount} to add to primary
        </Text>
      ) : null}
      <Text display="flex" alignItems="center">
        <Icon name="does-not-send" color="critical" mr="050" />
        {addedCount === 0
          ? 'All attributes already exist in primary'
          : `${formatNumber(existingCount)} already ${existingCount === 1 ? 'exists' : 'exist'} in primary`}
      </Text>
    </>
  );
}

/** The "N attributes" row with the per-card reading of the preview. */
function AttributeSummary({
  profileType,
  attributeCount,
  addedCount,
  hasSummary,
  onScrollToAdded,
}: AttributeSummaryProps) {
  let detail: React.ReactNode = null;
  switch (profileType) {
    case ProfileType.Primary:
      detail = '(all attributes will be saved)';
      break;
    case ProfileType.Secondary:
      detail = hasSummary ? (
        <SecondarySummary addedCount={addedCount} existingCount={attributeCount - addedCount} />
      ) : null;
      break;
    case ProfileType.Merged:
      detail =
        addedCount === 0 ? (
          'No attributes added'
        ) : (
          <Link display="flex" alignItems="center" onClick={onScrollToAdded}>
            <Icon name="check-circle" color="success" mr="050" display="inline-block" />
            {addedCount} added
          </Link>
        );
      break;
    default: {
      const _exhaustive: never = profileType;
      detail = _exhaustive;
    }
  }

  return (
    <PanelSection display="flex" alignItems="center" data-test="merge-customer-attributes-summary">
      <Heading level="3" text="product-h3" flex={1}>
        {attributeCount} {plur('attribute', attributeCount)}
      </Heading>
      <Text display="flex" alignItems="center" size="xs" color="subtle" gap="200">
        {detail}
      </Text>
    </PanelSection>
  );
}

type RowMarkerProps = {
  profileType: ProfileType;
  isFromSecondary: boolean;
  isMissingFromMerge: boolean;
};

/** The status coin beside a value: kept (green) or lost (red) on the secondary card, added (green) on the merged card. */
function RowMarker({ profileType, isFromSecondary, isMissingFromMerge }: RowMarkerProps) {
  if (profileType === ProfileType.Secondary) {
    return isFromSecondary || isMissingFromMerge ? (
      <Coin colorVariant="red" size="sm">
        <Icon name="does-not-send" />
      </Coin>
    ) : (
      <Coin colorVariant="green" size="sm">
        <Icon name="check-circle" />
      </Coin>
    );
  }
  if (profileType === ProfileType.Merged && isFromSecondary) {
    return (
      <Coin colorVariant="green" size="sm">
        <Icon name="check-circle" />
      </Coin>
    );
  }
  return null;
}

/**
 * A selected person's card body: who they are, how many attributes they have,
 * and the attribute list annotated with what the merge keeps, drops, or adds.
 * The primary card carries no annotations: everything it has is kept.
 */
export function CustomerProfileCard({
  profileType,
  profile,
  context,
  searchTerm,
  isLoadingMergedAttributes,
  mergedAttributes,
  mergeSummary,
  onSelectAnother,
}: CustomerProfileCardProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const cioId = profile.identifiers?.cio_id ?? undefined;
  const fromSecondary = attributeNamesFromOtherProfile(mergedAttributes, cioId);
  const attributes = mergeCardAttributes(
    profile,
    context.identifiers,
    profileType === ProfileType.Merged ? { fromSecondary } : {},
  );
  const filteredAttributes = filterAttributes(attributes, searchTerm);
  const missingFromMerge = attributesNotPresentInMerge(attributes, mergedAttributes);
  const addedCount = mergeSummary?.attributes.added ?? 0;
  const isAnnotated = profileType !== ProfileType.Primary && mergeSummary != null;

  const scrollToAdded = () => {
    listRef.current?.querySelector('[data-from-secondary]')?.scrollIntoView();
  };

  const rowProps = (attribute: ProfileAttribute) => {
    const isFromSecondary = fromSecondary.includes(attribute.key);
    const isMissingFromMerge = missingFromMerge.includes(attribute.key);
    return {
      isFromSecondary: isFromSecondary && profileType === ProfileType.Merged,
      isLineThrough: (isFromSecondary || isMissingFromMerge) && profileType === ProfileType.Secondary,
      icon: (
        <RowMarker
          profileType={profileType}
          isFromSecondary={isFromSecondary}
          isMissingFromMerge={isMissingFromMerge}
        />
      ),
    };
  };

  return (
    <>
      <ProfileHeader profile={profile} context={context} onSelectAnother={onSelectAnother} />

      {profileType !== ProfileType.Primary && isLoadingMergedAttributes ? (
        <PanelSection className={styles.attributeRows} display="flex" alignItems="center" justifyContent="center">
          <Spinner shouldShowLoadingText label="Finding attributes…" />
        </PanelSection>
      ) : (
        <>
          <AttributeSummary
            profileType={profileType}
            attributeCount={attributes.length}
            addedCount={addedCount}
            hasSummary={mergeSummary != null}
            onScrollToAdded={scrollToAdded}
          />
          <PanelInset overflowY="auto" className={styles.attributeList} ref={listRef}>
            <List styleType="none">
              {filteredAttributes.length === 0 ? (
                <Box textAlign="center" p="300">
                  <Paragraph text="product-h3" color="subtle">
                    No attributes for this {context.terms.peopleSingular} matching "{searchTerm}"
                  </Paragraph>
                </Box>
              ) : (
                filteredAttributes.map((attribute) => (
                  <ProfileAttributeRow
                    key={attribute.key}
                    attribute={attribute}
                    identifiers={context.identifiers}
                    isMultiIdentifier={context.isMultiIdentifier}
                    isEmailIdentified={context.isEmailIdentified}
                    peoplePlural={context.terms.peoplePlural}
                    identifiersSettingsHref={context.identifiersSettingsHref}
                    {...(isAnnotated ? rowProps(attribute) : {})}
                  />
                ))
              )}
            </List>
          </PanelInset>
        </>
      )}
    </>
  );
}
